import { getPortalSdk } from 'portal-sdk';
import { useQuery } from 'react-query';
import { emptyObject } from 'vet-bones/constants';
import { usePortalUser } from 'vet-bones/utils/portal';

import { Project } from 'src/store/state/projects';
import * as collaborationProvider from 'src/utils/collaboration/collaborationProvider';
import { duplicateProject } from 'src/utils/duplicate/duplicateProject';
import { decodeCloudContent } from 'src/utils/exportProject/encodeProject';

const CLOUD_PROJECT_QUERY_KEY = 'useCloudStorageProject';

export const getCloudProjectQueryKey = (
  projectId: string,
  isProfileLoading = false,
  isCloudStorageEnabled = true
) => [
  CLOUD_PROJECT_QUERY_KEY,
  isProfileLoading,
  isCloudStorageEnabled,
  projectId,
];

export const useCloudStorageProject = (projectId: string) => {
  const portalSdk = getPortalSdk();
  const { data: profileData, isLoading: isProfileLoading } = usePortalUser();
  const isCloudStorageEnabled = Boolean(
    profileData?.is_designer_storage_enabled
  );

  const { data, isLoading } = useQuery<{ project: Project | null } | null>(
    getCloudProjectQueryKey(projectId, isProfileLoading, isCloudStorageEnabled),
    async () => {
      if (!isCloudStorageEnabled || !portalSdk) {
        return { project: null };
      }

      try {
        const cloudProject = await portalSdk.getDesignerProject(projectId);
        if (cloudProject.content) {
          try {
            const data = decodeCloudContent(cloudProject.content);
            const project = Object.values(
              data?.projects?.projectsById || emptyObject
            )[0] as Project | undefined;

            const hasAccess =
              cloudProject.owner?.id === profileData?.id ||
              cloudProject.roles?.some(
                (role) => role.user?.id === profileData?.id
              );
            if (!project || !hasAccess) {
              return { project: null };
            }

            const newProject = duplicateProject(project, {
              id: cloudProject.id,
              // The content blob is authoritative for the connection: every
              // save path that writes the `connection_id` column also writes an
              // equal-or-fresher connectionId into the blob, but the collaboration
              // (Y.Doc) path persists content without reliably updating the column,
              // so the column can lag. Preferring the blob avoids hydrating Redux
              // with a stale connection that no longer resolves (which briefly
              // flashes the "choose a connection" dialog). Fall back to the column
              // when the blob has none.
              connectionId:
                project.connectionId || cloudProject.connection_id || undefined,
              orgDomain: cloudProject.org_domain || undefined,
              name: cloudProject.name,
              isCloudProject: true,
              dateLastUpdated: new Date(cloudProject.updated_at).getTime(),
              dateCreated: new Date(cloudProject.created_at).getTime(),
            });

            return { project: newProject };
          } catch {
            return { project: null };
          }
        }
        return { project: null };
      } catch {
        return { project: null };
      }
    },
    {
      enabled: !isProfileLoading,
      initialData: null,
      onError: () => {
        console.error(`Project ${projectId} not found`);
      },
      refetchOnMount: true,
      // When collaboration is active, the Y.Doc is the source of truth —
      // refetching from the cloud API on window focus would overwrite the
      // local project state with a potentially stale copy and cause GoJS
      // to rebuild the diagram (visible flash).
      refetchOnWindowFocus: () => !collaborationProvider.isConnected(projectId),
    }
  );

  return { data: data || null, isLoading: isProfileLoading || isLoading };
};
